import { LINK } from '@constants';
import { AlertTriangle, Button, Icon, Row, Typography } from '@viewup/ui';
import type {
  PostCampaignCardProps,
  PostingCampaignCardSummaryProps,
  PostViewingTypeCode,
} from 'app/viewing/_entities/post';
import {
  PostCampaignCard,
  PostCampaignCardStatus,
  PostCampaignStatus,
} from 'app/viewing/_entities/post';
import { DownloadGuidelineButton } from 'app/viewing/_features/post';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { DeductCard } from '../DeductCard';

dayjs.extend(duration);

type DraftCardProps = PostCampaignCardProps &
  PostingCampaignCardSummaryProps & {
    accountUuid: string;
    applicationDate: string;
    guidelineUrl: string;
    rewardPoint: number;
    submitLimitTime: Date;
    typeCode: PostViewingTypeCode;
    isGuidelineUpdated: boolean;
    postViewingRecordUuid: string;
  };

export const DraftCard = (props: DraftCardProps) => {
  const {
    description,
    rewardPoint,
    submitLimitTime,
    accountUuid,
    guidelineUrl,
    campaignUuid,
    options,
    isGuidelineUpdated,
    typeCode,
    postViewingRecordUuid,
  } = props;
  const router = useRouter();
  const [remainTime, setRemainTime] = useState(
    dayjs.duration(dayjs(submitLimitTime).diff(dayjs())),
  );

  const isNearDue = remainTime.hours() < 1;
  const isUnSubmitted = remainTime.asSeconds() <= 0;

  useEffect(() => {
    const interval = setInterval(() => {
      if (dayjs().isAfter(dayjs(submitLimitTime))) {
        clearInterval(interval);
        return;
      }

      setRemainTime(dayjs.duration(dayjs(submitLimitTime).diff()));
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const submit = () => {
    router.push(
      (() => {
        const params = new URLSearchParams({
          account: accountUuid,
          campaign: campaignUuid,
        });
        return `${LINK.VIEWING_POST_SUBMIT}?${params.toString()}`;
      })(),
    );
  };

  if (isUnSubmitted)
    return (
      <DeductCard
        applicationDate={props.applicationDate}
        campaignUuid={campaignUuid}
        deductionAmount={350}
        deductReason={PostCampaignStatus.NOT_SUBMITTED}
        name={props.name}
        rewardPoint={rewardPoint}
      />
    );

  return (
    <PostCampaignCard {...props}>
      <PostCampaignCard.Point point={rewardPoint} />
      <PostCampaignCard.Summary description={description} options={options} />
      <PostCampaignCard.Progress
        status={isNearDue ? PostCampaignCardStatus.NEAR_DUE : PostCampaignCardStatus.DRAFT}
      />
      <PostCampaignCard.BottomContent gap={8}>
        <Row bgColor='bg.secondary' br={8} justifyContent='space-between' px={12} py={8}>
          <Typography color='text.tertiary' fontType='body2.regular'>
            제출까지 남은 시간
          </Typography>
          <Typography color={isNearDue ? 'text.danger' : 'text.info'} fontType='body2.regular'>
            {remainTime.format('HH:mm:ss')}
          </Typography>
        </Row>

        {isGuidelineUpdated && (
          <Row alignItems='center' bgColor='bg.dangerSubtle' br={8} gap={4} px={16} py={8}>
            <Icon color='icon.danger' size='2XS' source={AlertTriangle} />
            <Typography color='text.danger' fontType='body2.regular'>
              해당 캠페인 가이드라인이 변경되었어요!
            </Typography>
          </Row>
        )}
        <Row gap={8}>
          <DownloadGuidelineButton
            accountUuid={accountUuid}
            campaignUuid={campaignUuid}
            css={{ flex: 1 }}
            guidelineUrl={guidelineUrl}
            isUpdated={isGuidelineUpdated}
            postViewingRecordUuid={postViewingRecordUuid}
            size='M'
            variant='outline.primary'
          />
          <Button className='submitButton' css={{ flex: 1 }} onClick={submit} size='M'>
            제출하기
          </Button>
        </Row>
      </PostCampaignCard.BottomContent>
    </PostCampaignCard>
  );
};
